import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useBuyer } from './BuyerContext';

// Action types
const SET_SELECTED_PRODUCTS = 'SET_SELECTED_PRODUCTS';
const REMOVE_FROM_SELECTED = 'REMOVE_FROM_SELECTED';
const TOGGLE_LIKE = 'TOGGLE_LIKE';
const SET_LIKED_PRODUCTS = 'SET_LIKED_PRODUCTS';
const SET_LOADING = 'SET_LOADING';
const CLEAR_ALL_SELECTED = 'CLEAR_ALL_SELECTED'; // ✅ NEW

// Initial state
const initialState = {
  selectedProducts: [],
  likedProducts: [],
  loading: true,
};

// Reducer
const productReducer = (state, action) => {
  switch (action.type) {
    case SET_SELECTED_PRODUCTS:
      return { ...state, selectedProducts: action.payload };

    case REMOVE_FROM_SELECTED:
      return {
        ...state,
        selectedProducts: state.selectedProducts.filter((p) => p.documentId !== action.payload),
      };

    case CLEAR_ALL_SELECTED: // ✅ NEW
      return { ...state, selectedProducts: [] };

    case SET_LIKED_PRODUCTS:
      return { ...state, likedProducts: action.payload };

    case TOGGLE_LIKE:
      const isLiked = state.likedProducts.includes(action.payload);
      return {
        ...state,
        likedProducts: isLiked
          ? state.likedProducts.filter((id) => id !== action.payload)
          : [...state.likedProducts, action.payload],
      };

    case SET_LOADING:
      return { ...state, loading: action.payload };

    default:
      return state;
  }
};

const ProductContext = createContext();
const ProductDispatchContext = createContext();

// Thunks
export const addToSelected = (fullProductWithVariants, buyerId) => async (dispatch, getState) => {
  console.log(fullProductWithVariants);
  if (!buyerId) {
    console.error('No buyerId provided');
    return;
  }

  const state = getState();
  const existingIndex = state.selectedProducts.findIndex((p) =>
    p.id === fullProductWithVariants.id &&
    p.serviceType === fullProductWithVariants.serviceType
  );

  const batch = writeBatch(db);
  let updatedSelected;
  let newDocRef;

  if (existingIndex > -1) {
      updatedSelected = [...state.selectedProducts];
      const existing = updatedSelected[existingIndex];

      const sizeMap = new Map();
      (existing.selectedVariants || []).forEach((v) => sizeMap.set(v.size, v.quantity));
      (fullProductWithVariants.selectedVariants || []).forEach((v) => sizeMap.set(v.size, v.quantity));

      const mergedVariants = Array.from(sizeMap.entries()).map(([size, qty]) => ({ size, quantity: qty }));

      updatedSelected[existingIndex] = {
        ...existing,
        selectedVariants: mergedVariants,
        updatedAt: new Date().toISOString(),
      };

      const oldDocRef = doc(db, 'selectedProducts', existing.documentId);
      batch.delete(oldDocRef);
      newDocRef = doc(db, 'selectedProducts', existing.documentId);
    } else {
      newDocRef = doc(collection(db, 'selectedProducts'));
      const newEntry = {
        ...fullProductWithVariants,
        selectedVariants: fullProductWithVariants.selectedVariants || [],
        addedAt: new Date().toISOString(),
        documentId: newDocRef.id,
      };
      updatedSelected = [...state.selectedProducts, newEntry];
    }

    dispatch({ type: SET_SELECTED_PRODUCTS, payload: updatedSelected });

    try {
      const mergedEntry = updatedSelected.find((p) =>
        p.id === fullProductWithVariants.id &&
        p.serviceType === fullProductWithVariants.serviceType
      );
      const {
        documentId: _ignoreDocId,
        addedAt: _ignoreAddedAt,
        ...productToSave
      } = mergedEntry || fullProductWithVariants;
      batch.set(newDocRef, {
        buyerId,
        productId: fullProductWithVariants.id,
        serviceType: fullProductWithVariants.serviceType,
        selectedVariants: mergedEntry?.selectedVariants || fullProductWithVariants.selectedVariants || [],
        ...productToSave,
        addedAt: new Date(),
      });

      await batch.commit();
} catch (error) {
      console.error('Error saving to Firebase:', error);
      dispatch({ type: SET_SELECTED_PRODUCTS, payload: state.selectedProducts });
    }
};

export const addToSelectedMultiple = (products, buyerId) => async (dispatch, getState) => {
  if (!Array.isArray(products) || !products.length) return;
  for (const product of products) {
    await addToSelected(product, buyerId)(dispatch, getState);
  }
};

export const removeFromSelected = (documentId, buyerId, productId) => async (dispatch, getState) => {
  if (!buyerId) return;

  try {
    let docRef;
    if (documentId) {
      docRef = doc(db, 'selectedProducts', documentId);
    } else if (productId) {
      const q = query(collection(db, 'selectedProducts'), where('buyerId', '==', buyerId), where('productId', '==', productId));
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        console.warn('No document found for productId:', productId);
        return;
      }
      docRef = snapshot.docs[0].ref;
    } else {
      console.warn('No documentId or productId provided for removal');
      return;
    }
    await deleteDoc(docRef);
    dispatch({ type: REMOVE_FROM_SELECTED, payload: documentId || productId });
  } catch (error) {
    console.error('Error removing from Firebase:', error);
    const prevState = getState();
    dispatch({ type: SET_SELECTED_PRODUCTS, payload: prevState.selectedProducts });
  }
};

// ✅ NEW THUNK
export const clearAllSelected = (buyerId) => async (dispatch, getState) => {
  if (!buyerId) return;

  try {
    const state = getState();
    const batch = writeBatch(db);

    state.selectedProducts.forEach((product) => {
      if (product.documentId) {
        batch.delete(doc(db, 'selectedProducts', product.documentId));
      }
    });

    await batch.commit();
    dispatch({ type: CLEAR_ALL_SELECTED });
  } catch (error) {
    console.error('Error clearing all selected:', error);
    // Revert: reload from Firestore
    const selectedQuery = query(
      collection(db, 'selectedProducts'),
      where('buyerId', '==', buyerId)
    );
    const snapshot = await getDocs(selectedQuery);
    const selectedList = snapshot.docs.map(doc => ({
      documentId: doc.id,
      ...doc.data().product,
      id: doc.data().productId,
      serviceType: doc.data().product?.serviceType || doc.data().serviceType || 'order',
    }));
    dispatch({ type: SET_SELECTED_PRODUCTS, payload: selectedList });
  }
};

export const toggleLike = (productId, buyerId) => async (dispatch, getState) => {
  if (!buyerId) return;

  const state = getState();
  const isLiked = state.likedProducts.includes(productId);

  dispatch({ type: TOGGLE_LIKE, payload: productId });

  try {
    if (isLiked) {
      const likedQuery = query(
        collection(db, 'likedProducts'),
        where('buyerId', '==', buyerId),
        where('productId', '==', productId)
      );
      const snapshot = await getDocs(likedQuery);
      snapshot.forEach(async (doc) => await deleteDoc(doc.ref));
    } else {
      await addDoc(collection(db, 'likedProducts'), {
        buyerId,
        productId,
        likedAt: new Date(),
      });
    }
  } catch (error) {
    console.error('Error updating like in Firebase:', error);
    dispatch({ type: TOGGLE_LIKE, payload: productId });
  }
};

// Provider
export const ProductProvider = ({ children }) => {
  const [state, dispatch] = useReducer(productReducer, initialState);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const getState = () => stateRef.current;
  const { buyer } = useBuyer();

  const actions = {
    addToSelected: (product) => {
      if (buyer?.buyerId) {
        addToSelected(product, buyer.buyerId)(dispatch, getState);
      }
    },
    addToSelectedMultiple: (products) => {
      if (buyer?.buyerId) {
        addToSelectedMultiple(products, buyer.buyerId)(dispatch, getState);
      }
    },
    removeFromSelected: (documentId) => {
      if (buyer?.buyerId) {
        removeFromSelected(documentId, buyer?.buyerId)(dispatch, getState);
      }
    },
    clearAllSelected: () => { // ✅ NEW
      if (buyer?.buyerId) {
        clearAllSelected(buyer.buyerId)(dispatch, getState);
      }
    },
    toggleLike: (id) => {
      if (buyer?.buyerId) {
        toggleLike(id, buyer.buyerId)(dispatch, getState);
      }
    },
  };

  useEffect(() => {
    const loadUserData = async () => {
      if (!buyer?.buyerId) return;

      try {
        dispatch({ type: SET_LOADING, payload: true });

        const likedQuery = query(collection(db, 'likedProducts'), where('buyerId', '==', buyer.buyerId));
        const likedSnapshot = await getDocs(likedQuery);
        const likedIds = likedSnapshot.docs.map((doc) => doc.data().productId);
        dispatch({ type: SET_LIKED_PRODUCTS, payload: likedIds });

        const selectedQuery = query(
          collection(db, 'selectedProducts'),
          where('buyerId', '==', buyer.buyerId)
        );
        const selectedSnapshot = await getDocs(selectedQuery);
        const selectedList = selectedSnapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: data.productId,
              documentId: doc.id,
              serviceType: data.serviceType || 'order',
              selectedVariants: data.selectedVariants || [],
              ...data,
            };
          })
          .filter((p) => p && p.id);

        dispatch({ type: SET_SELECTED_PRODUCTS, payload: selectedList });
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        dispatch({ type: SET_LOADING, payload: false });
      }
    };

    loadUserData();
  }, [buyer?.buyerId]);

  return (
    <ProductContext.Provider value={state}>
      <ProductDispatchContext.Provider value={actions}>
        {children}
      </ProductDispatchContext.Provider>
    </ProductContext.Provider>
  );
};

export const useProductState = () => {
  const context = useContext(ProductContext);
  if (!context) throw new Error('useProductState must be used within ProductProvider');
  return context;
};

export const useProductActions = () => {
  const context = useContext(ProductDispatchContext);
  if (!context) throw new Error('useProductActions must be used within ProductProvider');
  return context;
};